// Entry point: folder open/reconcile, save, drag reorder, toolbar & keyboard
// wiring, startup. Owns the shared `state` and DOM elements and passes them to
// the other modules explicitly — no module imports another's singletons, so
// there are no circular imports.
//
// Navigation is two-tier — openRoot (picker / reopen-last) → openDir (every
// folder switch) — serialized by one generation token (openGen, checked after
// every await in both) so a superseded navigation stops before committing
// anything. Disk mutation is exclusive app-wide: at most one disk-mutating
// operation exists at any instant — the save chain's current write or one
// withExclusiveOp scope (see its comment below) — and the only other
// concurrency is read-only (navigation, thumb jobs). state.dir is always the
// *current* folder; save/rename read it at call time.
import Sortable from '../sortable.complete.esm.js';
import { io, preloadAll, clearSession } from './thumbs.js';
import { initHoverPlay, armHover, cancelHoverPlay } from './hoverplay.js';
import { clampCols, clampRowH, initLayout } from './layout.js';
import { initPreview } from './preview.js';
import { initSelection } from './selection.js';
import { initViewer } from './viewer.js';
import { initHistory } from './history.js';
import { initRename } from './rename.js';
import { initTree } from './tree.js';
import { settings, initSettingsPanel } from './settings.js';
import { initHelp } from './help.js';
import { IMG, VID, SORT_FILE, HEADER, toggleBrowserFullscreen, verticalNeighbor, icon } from './util.js';

const grid = document.getElementById('grid');
const bar = document.getElementById('bar');
const landing = document.getElementById('landing');
const statusEl = document.getElementById('status');
const statusTextEl = document.getElementById('statusText');
const folderNameEl = document.getElementById('folderName');
const viewer = document.getElementById('viewer');
const opOverlay = document.getElementById('opOverlay');

// ---------- Test-only timing overrides (production-inert) ----------
// A test harness may set `globalThis.__MS_TEST_TIMINGS__` (via Playwright
// addInitScript, BEFORE any app module loads) to shrink the genuine wall-clock
// waits that dominate the regression suite. In production the global is never
// set, so every `?? <default>` below yields the exact production value —
// behavior is byte-for-byte identical. This is the one central definition;
// selection.js/tree.js read `longPressMs`, viewer.js reads `uiHideMs` and
// `ghostClickMs`, and gestures.js reads `dblTapMs` with the same
// `globalThis.__MS_TEST_TIMINGS__?.<key> ?? <default>` pattern at their own
// module scope. Recognised keys: longPressMs, sortDelayMs, uiHideMs,
// saveDebounceMs, ghostClickMs, dblTapMs.
const SAVE_DEBOUNCE_MS = globalThis.__MS_TEST_TIMINGS__?.saveDebounceMs ?? 200;
const SORT_DELAY_MS = globalThis.__MS_TEST_TIMINGS__?.sortDelayMs ?? 160;

const state = {
  root: null,         // FileSystemDirectoryHandle of the picked folder (idb-persisted)
  dir: null,          // current working directory handle (=== root at top level)
  path: [],           // segments from root to dir ([] = root)
  cols: clampCols(settings.get('cols')),
  displayMode: settings.get('displayMode'),
  rowH: clampRowH(settings.get('rowH')),
  preview: settings.get('preview'),
  loaded: false,
};

/* ---------- IndexedDB: remember last folder handle ---------- */
function idb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open('media-sorter', 1);
    r.onupgradeneeded = () => r.result.createObjectStore('handles');
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function idbSet(k, v) {
  const db = await idb();
  await new Promise((res, rej) => {
    const tx = db.transaction('handles', 'readwrite');
    tx.objectStore('handles').put(v, k);
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
}
async function idbGet(k) {
  const db = await idb();
  return new Promise((res, rej) => {
    const tx = db.transaction('handles', 'readonly');
    const rq = tx.objectStore('handles').get(k);
    rq.onsuccess = () => res(rq.result); rq.onerror = () => rej(rq.error);
  });
}
async function idbDel(k) {
  const db = await idb();
  await new Promise((res, rej) => {
    const tx = db.transaction('handles', 'readwrite');
    tx.objectStore('handles').delete(k);
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
}
// The remembered folder (IndexedDB 'last', offered via the "Reopen" button)
// turned out to be gone — stop remembering and stop offering it, so retrying
// doesn't just fail the same way again.
async function forgetLastFolder() {
  try { await idbDel('last'); } catch {}
  const reopenBtn = document.getElementById('reopenBtn');
  if (reopenBtn) reopenBtn.hidden = true;
}

async function ensurePermission(handle) {
  const opts = { mode: 'readwrite' };
  if ((await handle.queryPermission(opts)) === 'granted') return true;
  return (await handle.requestPermission(opts)) === 'granted';
}

// Re-read the open folder from disk (rename.js's renameAll resync after a
// failed rename, so the grid matches whatever half-completed on disk).
// ensurePermission normally only runs in openRoot — but this recovery path
// must re-check it too: if the site's permission was revoked mid-session,
// openFolder's own enumeration would just throw NotFoundError-shaped
// confusion instead of re-prompting.
async function resyncCurrentFolder() {
  if (!state.dir) return;
  if (!(await ensurePermission(state.dir))) { setStatus('Permission denied', 'error'); return; }
  await openFolder(state.dir);
}

/* ---------- Load a folder & reconcile order ---------- */
// Entry for a newly picked / reopened ROOT: hands it to the tree, persists it,
// then re-descends the remembered subpath (falling back to the root when a
// segment no longer resolves) and opens the resulting directory. Shares
// openDir's generation token, taken here at the very start and threaded
// through to openDir at the end, so a second openRoot/openDir beginning
// anywhere during these awaits supersedes this one cleanly instead of
// leaving state.root/tree pointed at one root and the grid at another.
async function openRoot(dir, subpath = []) {
  const gen = ++openGen;
  const granted = await ensurePermission(dir);
  if (gen !== openGen) return;
  if (!granted) { setStatus('Permission denied', 'error'); return; }
  // A dead handle must report + be forgotten without disturbing anything
  // already switched to (state.root, the tree panel, a running thumb job) —
  // and, if a newer open has since taken over, without stomping its status.
  const reportDeadRoot = async (e) => {
    if (gen !== openGen) return;
    console.error('openRoot: failed to read root', e);
    const isMissing = e?.name === 'NotFoundError';
    setStatus(isMissing ? 'Folder not found — it may have been moved or deleted' : 'Could not read folder', 'error');
    if (isMissing) await forgetLastFolder();
  };
  // A remembered root may be gone. Probe with a cheap read before touching
  // state.root or treeApi.setRoot (which wipes the tree panel to the new
  // root as its first step, ahead of its own scan) — so the common dead-root
  // case reports while the previous root/tree/state are left exactly as they
  // were, instead of a throw leaving the app half-switched.
  try { await dir.keys().next(); } catch (e) { await reportDeadRoot(e); return; }
  if (gen !== openGen) return;
  state.root = dir;
  try { await treeApi.setRoot(dir); } catch (e) { await reportDeadRoot(e); return; }
  if (gen !== openGen) return;
  try { await idbSet('last', dir); } catch {}
  let cur = dir, path = [];
  for (const seg of subpath) {
    if (gen !== openGen) return;
    try { cur = await cur.getDirectoryHandle(seg); path = [...path, seg]; }
    catch { cur = dir; path = []; break; }
  }
  if (gen !== openGen) return;
  await openDir(cur, path, gen);
}

// Navigate the app to a directory (the root or a descendant). The single path
// every navigation goes through: tree clicks, openRoot, post-FS-op re-descends.
// Shares the header's generation-token serialization; `gen` defaults to
// minting fresh (direct callers like tree clicks), openRoot passes its own so
// the two share one token end-to-end.
let openGen = 0;
async function openDir(dir, path, gen = ++openGen) {
  // Block new saves and undo/redo for the whole switch — state.dir can flip to
  // the new folder before render() repopulates the grid with its items, and a
  // save or undo landing in that gap would write the wrong folder's names into
  // the wrong sort file. Restored below: on success by openFolder (once
  // rendered), on failure by the rollback branch (the old folder's grid is
  // still live, so it needs it back).
  state.loaded = false;
  // A reorder's debounced save may still be pending against the folder we're
  // leaving — flush it now, while state.dir/grid still belong to that folder.
  await drainSaves();
  if (gen !== openGen) return;
  const prevPath = state.path;
  state.path = path;
  clearSession();   // the session thumb map is bare-name keyed — folders would collide
  await treeApi.setCurrent(path);
  if (gen !== openGen) return;
  const ok = await openFolder(dir, gen);
  if (gen !== openGen) return;   // superseded (or fell back to the root) — that navigation owns state now
  if (ok) {
    settings.set('lastSubpath', path);
  } else {
    // Failed without navigating elsewhere: state.dir/grid still show the old
    // folder, so roll the path/breadcrumb/highlight back to match.
    state.path = prevPath;
    await treeApi.setCurrent(prevPath);
    state.loaded = true;
  }
}

// `dir` may be a handle remembered from a previous session (the "Reopen"
// button, restored from IndexedDB) — the folder it points to can have been
// moved/deleted/unmounted since, in which case listing it throws NotFoundError.
// Caught here (rather than left uncaught) so a dead folder reports a status
// instead of crashing; a dead subfolder falls back to opening the root.
// Returns true when this folder's content was rendered.
async function openFolder(dir, gen = ++openGen) {
  // Collect media file handles; note any visible files the app doesn't handle.
  const files = new Map();
  const others = [];
  try {
    for await (const [name, h] of dir.entries()) {
      if (h.kind !== 'file') continue;
      if (IMG.test(name) || VID.test(name)) files.set(name, h);
      else if (!name.startsWith('.')) others.push(name);
    }
  } catch (e) {
    console.error('openFolder: failed to read directory', e);
    const isMissing = e?.name === 'NotFoundError';
    setStatus(isMissing ? 'Folder not found — it may have been moved or deleted' : 'Could not read folder', 'error');
    if (isMissing && gen === openGen) {
      if (!state.root || dir === state.root) {
        await forgetLastFolder();
      } else {
        // The subfolder vanished — fall back to the root. The rebuild itself
        // can throw (the whole root may be gone); then forget it instead of
        // leaking an unhandled rejection out of a click handler.
        try { await treeApi.rebuild(); await openDir(state.root, []); }
        catch { await forgetLastFolder(); }
      }
    }
    return false;
  }
  if (gen !== openGen) return false;   // superseded while enumerating

  state.dir = dir;
  state.loaded = false;
  updateOtherFiles(others.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));

  // Read existing order.
  let order = [];
  try {
    const fh = await dir.getFileHandle(SORT_FILE);
    const text = await (await fh.getFile()).text();
    const lines = text.split(/\r?\n/);
    if (lines[0]?.trim() === HEADER) lines.shift();
    order = lines.filter(n => n.length);
  } catch { /* no sort file yet */ }

  // Ordered items first, then any new files (natural sort), stale entries dropped.
  const items = [];
  const seen = new Set();
  for (const name of order) {
    if (files.has(name) && !seen.has(name)) { seen.add(name); items.push(mkItem(name, files.get(name))); }
  }
  [...files.keys()]
    .filter(n => !seen.has(n))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .forEach(n => items.push(mkItem(n, files.get(n))));

  if (gen !== openGen) return false;   // superseded while reading the sort file
  render(items);
  historyApi.resetHistory();
  state.loaded = true;

  landing.hidden = true;
  bar.hidden = false;
  // Breadcrumb as individual .seg spans so styles.css can shrink/hide segments
  // when the toolbar gets narrow. Descending flex-shrink (root highest, leaf 1)
  // makes the root end compress first; the full path stays in the title.
  const segs = [state.root?.name ?? dir.name, ...state.path];
  folderNameEl.replaceChildren(...segs.map((name, i) => {
    const s = document.createElement('span');
    s.className = 'seg';
    s.textContent = name;
    s.style.flexShrink = segs.length - i;
    return s;
  }));
  folderNameEl.title = segs.join(' / ');
  setStatus(items.length + ' items');
  return true;
}

function mkItem(name, handle) {
  return { name, handle, kind: VID.test(name) ? 'video' : 'image' };
}

/* ---------- Render grid ---------- */
function render(items) {
  previewApi.clear();
  cancelHoverPlay();   // don't leak a hover-play targeting a cell about to be discarded
  // Purge Sortable's internal selection while the cells are still attached —
  // utils.deselect early-returns on detached nodes, and any entry left in its
  // multiDragElements list would be resurrected into the grid on the next drop.
  for (const c of grid.querySelectorAll('.sjs-selected')) Sortable.utils.deselect(c);
  selectionApi.clear();
  io.disconnectAll();
  grid.replaceChildren();
  for (const item of items) grid.appendChild(makeCell(item));
  layout.applyLayout();
  preloadAll(grid.children);   // eager whole-folder thumbnail generation (viewport still prioritises order)
}

function makeCell(item) {
  const cell = document.createElement('div');
  cell.className = 'cell';
  cell.dataset.name = item.name;
  cell.title = item.name;
  cell._item = item;

  const img = document.createElement('img');
  img.draggable = false;
  // decoding='sync', deliberately: 'async' opts the thumb into Chromium's
  // checker-imaging (cc only defers/checkers images whose developer-specified
  // decoding mode is async — cc/tiles/checker_image_tracker.cc). Deferred means
  // the tile composites BLANK first and repaints when the async decode lands;
  // under decode-cache pressure that follow-up invalidation can be lost, leaving
  // cells black until an unrelated repaint (hover/scroll/resize). A cache-hit
  // reopen assigns a whole viewport of srcs in one frame and hit exactly that
  // (see test/repro-cache-render.mjs). 'sync' pins "present atomically with the
  // frame": the decode still runs on raster workers, never the main thread — it
  // just can't be deferred past the frame. Don't "fix" this back to async or
  // drop it to auto (auto works today only because unspecified isn't checkered).
  img.decoding = 'sync';
  cell.appendChild(img);
  cell._media = img;

  if (item.kind === 'video') {
    const badge = document.createElement('div');
    badge.className = 'badge'; badge.innerHTML = icon('play');
    cell.appendChild(badge);
  }
  io.observe(cell);
  return cell;
}

/* ---------- Save .XnViewSort (auto, debounced) ---------- */
let saveTimer = null;
// Promise of whatever save(s) are currently outstanding (chained so a save
// started while another is still writing runs after it, never concurrently
// with it), or null once the chain drains. Distinct from saveTimer: a save
// can be in flight (past its debounce, awaiting file I/O) with no timer
// pending, so drainSaves must track this separately from the timer.
let saveInFlight = null;
function scheduleSave() {
  if (!state.loaded) return;
  // Input is fully blocked during an exclusive op (see withExclusiveOp), so
  // nothing external should be able to reach this while opLocked — an
  // exclusive op that needs a sort-file write does it explicitly via
  // writeSortFile, inside its own scope. If this fires anyway, it's a bug.
  if (opLocked) { console.warn('scheduleSave called while an exclusive op is locked'); return; }
  clearTimeout(saveTimer);
  setStatus('Saving…', 'saving');
  saveTimer = setTimeout(startSave, SAVE_DEBOUNCE_MS);   // 200ms in production; see SAVE_DEBOUNCE_MS
}
function startSave() {
  saveTimer = null;
  const prior = saveInFlight;
  const p = (prior ? prior.then(save) : save())
    .finally(() => { if (saveInFlight === p) saveInFlight = null; });
  saveInFlight = p;
}
// Drain every pending/in-flight save, looping rather than checking once: a
// save can chain a new one onto saveInFlight (scheduleSave while one is
// mid-write) during the very await below, which a single check-then-await
// would miss and return early against. withExclusiveOp calls this after
// setting opLocked (which stops new saves from arming), so it empties in one
// pass; openDir calls it with nothing stopping new arrivals, so it loops
// until it truly empties.
async function drainSaves() {
  while (saveTimer !== null || saveInFlight) {
    if (saveTimer !== null) { clearTimeout(saveTimer); startSave(); }
    if (saveInFlight) await saveInFlight;
  }
}
// The actual sort-file write, no status side effects — save() is the
// debounced-auto-save caller; exclusive ops call this directly with their own
// snapshots so their final status persists instead of being stomped by a
// trailing "Saved ✓" (see withExclusiveOp).
async function writeSortFile(dir, names) {
  const fh = await dir.getFileHandle(SORT_FILE, { create: true });
  const w = await fh.createWritable();
  await w.write(HEADER + '\n' + names.join('\n') + '\n');
  await w.close();
}
async function save() {
  if (!state.dir) return;
  // Snapshot both dir and names synchronously: whatever awaits below, this
  // save stays self-consistent (folder X's names into folder X's file).
  const dir = state.dir;
  const names = [...grid.children].map(c => c.dataset.name);
  try {
    await writeSortFile(dir, names);
    setStatus('Saved ✓');
  } catch (e) {
    console.error(e);
    setStatus('Save failed', 'error');
  }
}
const toastsEl = document.getElementById('toasts');
// Errors get a sticky, dismissible toast (out of flow — never shifts #bar/#main)
// instead of the missable, stompable corner readout. Identical messages dedupe
// rather than stack, so a repeated failure doesn't pile up copies.
function showError(msg) {
  for (const t of toastsEl.children) if (t.dataset.msg === msg) return;
  const t = document.createElement('div');
  t.className = 'toast'; t.dataset.msg = msg;
  t.setAttribute('role', 'alert');
  t.textContent = msg;
  const x = document.createElement('button');
  x.className = 'toast-x'; x.innerHTML = icon('x'); x.title = 'Dismiss';
  t.appendChild(x);
  t.onclick = () => t.remove();
  toastsEl.appendChild(t);
}
// cls==='error' routes to a toast and returns the readout to a neutral resting
// state, so a mid-op 'Saving…'/'Moving…' doesn't linger looking live after the
// failure. title mirrors the (possibly ellipsis-truncated) text.
function setStatus(msg, cls = '') {
  if (cls === 'error') {
    showError(msg);
    msg = state.dir ? grid.children.length + ' items' : '';
    cls = '';
  }
  statusTextEl.textContent = msg; statusEl.className = cls; statusEl.title = msg;
}

/* ---------- App-wide exclusive-operation scope ---------- */
// tree.js's long-running move ops (doPaste/doRename/moveSelectedTo/
// doNewFolder) and rename.js's renameAll (minutes on big folders) run their
// disk-mutating work — including whatever tree refresh/repoint follows the
// mutation — inside withExclusiveOp(fn). Rather than disabling entry points by
// enumeration (the old withFsLock's approach, which this branch's own race
// holes proved doesn't scale), a full-window overlay physically blocks all
// user input for fn's whole duration: pointer events can't reach the grid,
// toolbar, or tree, and the shared keydown listener below no-ops. (The full
// design + racing matrix lived in a temporary doc, dropped once implemented:
// `git show 00da857:tree-lock-simplification.md`.)
// Draining pending/in-flight saves happens *before* fn runs, and
// scheduleSave refuses to arm a new one while locked (see scheduleSave) — an
// op that needs a sort-file write does it explicitly via writeSortFile,
// awaited, inside its own scope.
let opLocked = false;
async function withExclusiveOp(fn) {
  if (opLocked) throw new Error('withExclusiveOp: already locked');
  opLocked = true;
  opOverlay.hidden = false;
  try {
    await drainSaves();
    return await fn();
  } finally {
    opLocked = false;
    opOverlay.hidden = true;
  }
}

/* ---------- Unhandled-files note (⚠ icon + hover popup) ---------- */
const otherWarn = document.getElementById('otherWarn');
const otherPop = document.getElementById('otherPop');

function updateOtherFiles(names) {
  otherWarn.hidden = names.length === 0;
  otherWarn.classList.remove('open');
  otherPop.replaceChildren();
  if (!names.length) return;
  const title = document.createElement('b');
  title.textContent = names.length + (names.length === 1 ? ' file' : ' files') +
    ' in this folder ' + (names.length === 1 ? 'is' : 'are') + ' not shown (not an image or video):';
  otherPop.appendChild(title);
  for (const n of names) {
    const d = document.createElement('div');
    d.textContent = n;
    otherPop.appendChild(d);
  }
}
// Hover shows the popup on mouse; tap toggles it on touch.
otherWarn.addEventListener('click', (e) => { e.stopPropagation(); otherWarn.classList.toggle('open'); });
addEventListener('click', () => otherWarn.classList.remove('open'));

/* ---------- Module wiring ---------- */
// preview ↔ viewer would be a circular import; both are created here instead,
// with the one function each needs from the other passed through a closure.
const settingsPanel = initSettingsPanel();
const layout = initLayout({ state, grid, colsLabel: document.getElementById('colsLabel'), settingsPanel });
// onCurrent: the active cell (curCell) drives grid hover-play, but only while
// the preview panel is closed — with it open the panel plays the video instead.
const previewApi = initPreview({
  state, grid, viewer, openViewer: (item) => viewerApi.open(item), settingsPanel,
  onCurrent: (cell) => { if (!state.preview) armHover(cell); },
});
// Help panel (?, and a button in the ⚙ settings panel). Wired after layout and
// preview so its settings section lands below theirs.
const helpApi = initHelp({ settingsPanel });
// The floating cancel fob: visible whenever the selection is non-empty, clears
// it on press. selection.js emits every membership change through onChange.
const selCancel = document.getElementById('selCancel');
const selCount = document.getElementById('selCount');
const selectionApi = initSelection({
  grid,
  onChange: (size) => { selCancel.hidden = size === 0; selCount.textContent = size > 1 ? size : ''; },
});
selCancel.onclick = () => selectionApi.clear();
const viewerApi = initViewer({ grid, viewer, cancelHoverPlay });
const historyApi = initHistory({ grid, scheduleSave });
// tree.js is wired before rename.js so rename.js's thumb-job gate can take
// treeApi.isThumbBusy directly, instead of a thunk for a not-yet-created API.
const treeApi = initTree({
  state, grid, setStatus, writeSortFile,
  resetHistory: historyApi.resetHistory,
  clearPreview: previewApi.clear,
  selection: selectionApi,
  // Same purge render() does: clear Sortable's internal multi-drag list while
  // the cells are still attached, so cells the tree is about to detach can't
  // be resurrected into the grid by the next drop.
  purgeDragState: () => { for (const c of grid.querySelectorAll('.sjs-selected')) Sortable.utils.deselect(c); },
  openDir,
  withExclusiveOp,
});
initRename({
  state, grid, setStatus, writeSortFile,
  resetHistory: historyApi.resetHistory,
  resync: resyncCurrentFolder,
  withExclusiveOp,
  isThumbBusy: treeApi.isThumbBusy,
});

/* ---------- Drag reorder ---------- */
// MultiDrag is a dumb drag engine here — the app owns selection (selection.js
// writes the styled `.selected`); Sortable only ever touches its own unstyled,
// app-invisible `sjs-selected`. `avoidImplicitDeselect` drops its document-level
// deselect-on-outside-tap listener (we don't want it, and the purge below
// keeps its list in sync anyway).
const sortable = new Sortable(grid, {
  draggable: '.cell',
  multiDrag: true,
  selectedClass: 'sjs-selected',   // intentionally unstyled + Sortable-internal; the app never reads it
  avoidImplicitDeselect: true,
  animation: 150,
  delay: SORT_DELAY_MS,          // 160ms in production; see SORT_DELAY_MS (tests scale it with longPressMs to keep the drag-wins-vs-long-press boundary)
  delayOnTouchOnly: true,
  touchStartThreshold: 6,
  fallbackTolerance: 16,        // after the hold delay, require deliberate finger travel before a drag starts (so a small drift on a selected group never drags)
  forceFallback: true,          // consistent touch dragging across the grid
  scroll: grid,
  onStart: () => { previewApi.clear(); cancelHoverPlay(); document.body.classList.add('dragging'); },
  onEnd: () => { document.body.classList.remove('dragging'); historyApi.pushHistory(); scheduleSave(); },
  onSort: scheduleSave,
});

// Sync Sortable's internal selection to the app's right before a drag arms.
// Capture phase so it runs before Sortable's own bubble-phase pointerdown
// (for mouse, that handler synchronously freezes the multi-drag list). First
// purge Sortable's list (its tap-toggle leaves stray attached entries), then,
// only if the pressed cell is app-selected, re-select the whole app selection
// so the armed drag becomes a group drag. Pressing an unselected cell drags
// just that cell and leaves the app selection alone (file-manager behaviour).
// Runs for mouse and touch alike — one querySelectorAll, cheap.
grid.addEventListener('pointerdown', (e) => {
  for (const c of grid.querySelectorAll('.sjs-selected')) Sortable.utils.deselect(c);
  const cell = e.target.closest?.('.cell');
  if (cell && selectionApi.has(cell)) {
    for (const c of selectionApi.cells()) Sortable.utils.select(c);
  }
}, true);

initHoverPlay();   // grid video hover-play (armed via preview.js's onCurrent hook)

/* ---------- Controls ---------- */
// Don't let a pointer-clicked chrome button keep keyboard focus. A focused
// <button> natively fires on Enter/Space, so a toolbar/dialog button left
// focused after a click would double-act on the grid's own Enter (open viewer)
// / Space (select) shortcuts — and hold a stray focus ring, which is just noise
// for a mouse click. Blur it so focus falls back to <body>. Keyboard Tab focus
// is untouched, so :focus-visible a11y still works.
//
// Two deliberate constraints:
//  - Scope: only the app chrome around the grid (toolbar, settings, dialogs,
//    the selection/thumb-job fobs). The fullscreen viewer runs its own keyboard
//    handling and the video player's scrub bar has load-bearing pointer state,
//    so both are left alone.
//  - Deferred (queueMicrotask), not blurred inline: doing it mid-click-dispatch
//    reenters focus during the button's own handler (which may open the viewer
//    / a dialog and move focus itself); blurring the clicked button only after
//    the click settles keeps those flows intact. preventDefault on mousedown was
//    rejected for the same reason — it also breaks the scrub-drag / Sortable
//    pointer machinery.
// Capture phase so it still sees clicks whose handlers stopPropagation (the cog).
const CHROME_FOCUS_SEL = '#bar, #settingsPanel, .dlg, #selCancel, #thumbJob';
addEventListener('click', (e) => {
  const btn = e.target.closest?.('button');
  if (btn && btn.closest(CHROME_FOCUS_SEL)) queueMicrotask(() => btn.blur());
}, true);

document.getElementById('zoomIn').onclick = layout.zoomIn;
document.getElementById('zoomOut').onclick = layout.zoomOut;
document.getElementById('openBtn').onclick = pick;
document.getElementById('fsBtn').onclick = toggleBrowserFullscreen;

grid.addEventListener('wheel', (e) => {
  if (!e.ctrlKey) return;
  e.preventDefault();
  e.deltaY < 0 ? layout.zoomIn() : layout.zoomOut();
}, { passive: false });

const ARROW_DIR = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };

addEventListener('keydown', (e) => {
  if (opLocked) return;   // an exclusive op is running — all input is inert
  if (!viewer.hidden) return;   // handled by the viewer's own capture-phase listener
  // Fields (rename dialog input, the settings selects, …) own every keystroke —
  // stand down completely while one has focus. (The normal grid context has
  // focus on <body>: cells aren't focusable, and app.js blurs chrome buttons
  // after a pointer click, so this only bails while focus genuinely sits on a
  // control.)
  const t = e.target;
  if (t && (t.closest?.('input, textarea, select') || t.isContentEditable)) return;
  // A Tab-focused button/link is narrower: it only consumes Enter/Space (native
  // activation — running the grid's Enter/Space too would double-act on one
  // press). Any OTHER real keypress means the user is driving the grid again,
  // so release the control's focus and fall through to the shortcut — no dead
  // keyboard state where a focused button silences the whole grid. Escape's
  // entire job in that state is the release (the staged selection/cursor
  // clearing resumes on the next press). Tab and bare modifiers are exempt:
  // Tab keeps native traversal, and pressing Shift to begin a Shift+Tab must
  // not yank focus first.
  const ctl = t?.closest?.('button, a[href]');
  if (ctl) {
    if (e.key === 'Enter' || e.key === ' ') return;
    if (e.key === 'Tab' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') return;
    ctl.blur();
    if (e.key === 'Escape') return;
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); e.shiftKey ? historyApi.redo() : historyApi.undo(); return; }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); historyApi.redo(); return; }
  if (e.ctrlKey || e.metaKey) return;
  const dir = ARROW_DIR[e.key];
  if (dir) {
    e.preventDefault();
    e.shiftKey ? moveSelection(dir) : previewApi.navigate(dir);
  }
  // Space selects / Shift+Space range-selects the active cell, mirroring
  // click / Shift+click. (Ctrl+Space was rejected: Linux IMEs grab it.)
  else if (e.key === ' ') { e.preventDefault(); const c = previewApi.current() || previewApi.focusFirstVisible(); if (c) (e.shiftKey ? selectionApi.selectRange : selectionApi.select)(c); }
  else if (e.key === 'Enter') { const c = previewApi.current() || previewApi.focusFirstVisible(); if (c) viewerApi.open(c._item); }
  else if (e.key === '+' || e.key === '=') layout.zoomIn();
  else if (e.key === '-' || e.key === '_') layout.zoomOut();
  else if (e.key === 'f' || e.key === 'F') toggleBrowserFullscreen();
  else if (e.key === 'p' || e.key === 'P') previewApi.togglePreview();
  else if (e.key === '?') helpApi.open();
  // Escape is staged: clear the selection first, then (a second press) the
  // preview cursor. `!selCancel.hidden` ⟺ the selection is non-empty (onChange
  // keeps the fob's visibility in sync).
  else if (e.key === 'Escape') { if (!selCancel.hidden) selectionApi.clear(); else previewApi.setCurrent(null); }
});

// Shift+Arrow: relocate the whole selected set, cursor-as-destination. The
// selected block is lifted and dropped adjacent to the active cursor on the
// arrow side — before it (Left), after it (Right), or one row above/below it
// (Up/Down). When the cursor is itself part of the selection (dropping onto
// itself is meaningless), fall back to nudging the block one step in `dir`.
// Reuses the drag reorder path: move the nodes, then pushHistory + scheduleSave.
function moveSelection(dir) {
  const cursor = previewApi.current() || previewApi.focusFirstVisible();
  const moving = selectionApi.cells();   // DOM order
  if (!cursor || moving.length === 0) return;
  const movingSet = new Set(moving);
  const kids = [...grid.children];
  const others = kids.filter(c => !movingSet.has(c));
  if (others.length === 0) return;   // everything is selected — nowhere to move

  const forward = dir === 'right' || dir === 'down';
  let ins;   // insertion index into `others`
  if (dir === 'left' || dir === 'right') {
    if (movingSet.has(cursor)) {
      // Nudge: block position among `others` is the count of others before it.
      const pos = new Map(kids.map((c, i) => [c, i]));
      const firstIdx = pos.get(moving[0]);
      const p = others.filter(o => pos.get(o) < firstIdx).length;
      ins = forward ? p + 1 : p - 1;
    } else {
      const ci = others.indexOf(cursor);
      ins = forward ? ci + 1 : ci;
    }
  } else {
    // Up/Down: land at the row above/below the cursor, among non-moving cells.
    const vn = verticalNeighbor(cursor, others, dir);
    ins = vn ? others.indexOf(vn) : (forward ? others.length : 0);
  }
  ins = Math.max(0, Math.min(others.length, ins));

  const newOrder = [...others.slice(0, ins), ...moving, ...others.slice(ins)];
  if (newOrder.every((c, i) => c === kids[i])) return;   // no change
  const frag = document.createDocumentFragment();
  for (const c of newOrder) frag.appendChild(c);
  grid.appendChild(frag);
  historyApi.pushHistory();
  scheduleSave();
  cursor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/* ---------- Folder picking / startup ---------- */
async function pick() {
  try {
    const dir = await window.showDirectoryPicker({ mode: 'readwrite', id: 'media-sorter' });
    await openRoot(dir);
  } catch (e) {
    if (e && e.name !== 'AbortError') console.error(e);
  }
}

document.getElementById('pickBtn').onclick = pick;

async function init() {
  if (!window.showDirectoryPicker) {
    const el = document.getElementById('unsupported');
    el.style.display = 'block';
    el.innerHTML =
      'The File System Access API isn’t available here.<br>' +
      'secure context: <b>' + window.isSecureContext + '</b> · ' +
      'origin: <b>' + location.origin + '</b><br>' +
      (window.isSecureContext
        ? 'Origin looks OK — this browser likely disables the API (e.g. Brave, or an ungoogled/de-Googled Chromium). Try Google Chrome or Edge.'
        : 'Not a secure context — open the page via <b>http://localhost:8777</b> or <b>http://127.0.0.1:8777</b> (not a LAN IP).');
    document.getElementById('pickBtn').disabled = true;
    return;
  }
  layout.applyLayout();
  try {
    const last = await idbGet('last');
    if (last) {
      const btn = document.getElementById('reopenBtn');
      btn.hidden = false;
      btn.innerHTML = icon('history');
      btn.append(' Reopen “' + last.name + '”');  // text node — no HTML injection from folder name
      btn.onclick = () => openRoot(last, settings.get('lastSubpath') || []);
    }
  } catch {}
}
init();
